export type FeedbackMode = 'element' | 'page' | 'free';
export type FeedbackStatus = 'new' | 'resolved';
export type FeedbackCategory = 'bug' | 'inconvenient' | 'ugly' | 'slow' | 'typo' | 'other';
export type FeedbackTheme = 'light' | 'dark';
export type FeedbackSeverity = 1 | 2 | 3 | 4 | 5;

export interface FeedbackEnv {
  browser: string;
  os: string;
  theme: FeedbackTheme;
  viewport: string;
}

export interface FeedbackAuthor {
  id: string;
  name: string;
}

export interface Feedback {
  id: number;
  mode: FeedbackMode;
  status: FeedbackStatus;
  comment: string;
  severity: FeedbackSeverity;
  category: FeedbackCategory;
  route: string | null;
  url: string | null;
  testId: string | null;
  selector: string | null;
  instanceId: string | null;
  columnId: string | null;
  elementText: string | null;
  env: FeedbackEnv;
  author: FeedbackAuthor;
  isMine: boolean;
  createdAt: string;
  resolvedAt: string | null;
  resolutionNote: string | null;
}

export interface FeedbackCreatePayload {
  mode: FeedbackMode;
  comment: string;
  severity: FeedbackSeverity;
  category: FeedbackCategory;
  route?: string | null;
  url?: string | null;
  testId?: string | null;
  selector?: string | null;
  instanceId?: string | null;
  columnId?: string | null;
  elementText?: string | null;
  browser: string;
  os: string;
  theme: FeedbackTheme;
  viewport: string;
}

export interface FeedbackStatusPayload {
  status: FeedbackStatus;
  resolutionNote?: string | null;
}

export interface FeedbackListResponse {
  data: Feedback[];
  meta: {
    page: number;
    limit: number;
  };
}

export interface FeedbackTranscription {
  text: string;
}

export const FEEDBACK_CATEGORIES: { value: FeedbackCategory; label: string }[] = [
  { value: 'bug', label: 'Bug' },
  { value: 'inconvenient', label: 'Inconvenient' },
  { value: 'ugly', label: 'Ugly' },
  { value: 'slow', label: 'Slow' },
  { value: 'typo', label: 'Typo' },
  { value: 'other', label: 'Other' },
];

export const FEEDBACK_SEVERITIES: { value: FeedbackSeverity; label: string }[] = [
  { value: 1, label: 'Mild' },
  { value: 2, label: 'Meh' },
  { value: 3, label: 'Annoying' },
  { value: 4, label: 'Painful' },
  { value: 5, label: 'Rage' },
];

export const FEEDBACK_LIMITS = {
  comment: 2000,
  elementText: 200,
  selector: 1000,
  resolutionNote: 500,
  audioSeconds: 60,
  audioBytes: 2 * 1024 * 1024,
  listLimit: 200,
} as const;

export const FEEDBACK_GOTO_STORAGE_PREFIX = 'feedback-goto:';
/** Each Go to element click is its own localStorage entry `feedback-goto:<id>:<nonce>`, so clicks never overwrite
 * each other; `feedback-goto:<id>` without a nonce is the single shared record of earlier versions, still read. */
export const FEEDBACK_GOTO_ATTEMPT_SEPARATOR = ':';
export const FEEDBACK_GOTO_QUERY_PARAM = 'fb';
export const FEEDBACK_GOTO_TTL_MS = 60_000;
